// src/components/CustomerCartDrawer.jsx
import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaTrashAlt, FaPlus, FaMinus } from "react-icons/fa";

/**
 * CustomerCartDrawer - right-side drawer for logged-in user's cart
 *
 * Props:
 *  - show: boolean - open/closed
 *  - onClose: fn - called when drawer should close
 *  - onCartChange: fn(count) - notify header of number of cart entries
 */
const CustomerCartDrawer = ({ show, onClose, onCartChange }) => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [totalPrice, setTotalPrice] = useState("0.00");
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const user = JSON.parse(sessionStorage.getItem("active-user")) || null;

  const getShortDescription = (text, wordLimit) => {
    if (!text) return "";
    const words = text.trim().split(/\s+/);
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(" ") + "...";
  };

  // fetch cart + addresses
  const fetchCartAndAddresses = async () => {
    if (!user?.id) {
      setCartItems([]);
      setTotalPrice("0.00");
      setAddresses([]);
      if (onCartChange) onCartChange(0);
      return;
    }

    try {
      const resp = await axios.get(
        `https://goldenhides.in/api/user/mycart?userId=${user.id}`
      );
      const data = resp.data || {};
      setCartItems(data.cartData || []);
      setTotalPrice(Number(data.totalCartPrice || 0).toFixed(2));
      if (onCartChange) onCartChange((data.cartData || []).length);
    } catch (err) {
      console.error("fetchCart error", err);
      toast.error("Failed to load cart", {
        position: "top-center",
        autoClose: 1200,
      });
    }

    // load addresses from user object (if present in session) or fetch user again
    try {
      const sessUser = JSON.parse(sessionStorage.getItem("active-user"));
      if (sessUser?.deliveryAddresses) {
        const actives = (sessUser.deliveryAddresses || []).filter(
          (a) => a.status === "Active"
        );
        setAddresses(actives);
      } else {
        // optional: fetch user details from server if not in session
        const uResp = await axios.get(
          `https://goldenhides.in/api/user/${user.id}`
        );
        const uData = uResp.data;
        const actives = (uData.deliveryAddresses || []).filter(
          (a) => a.status === "Active"
        );
        setAddresses(actives);
      }
    } catch (err) {
      // ignore - addresses optional
      console.warn("address fetch failed", err);
    }
  };

  useEffect(() => {
    if (show) fetchCartAndAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // remove item
  const removeItem = async (cartId) => {
    try {
      const res = await fetch(
        `https://goldenhides.in/api/user/mycart/remove?cartId=${cartId}`,
        { method: "GET" }
      );
      const body = await res.json();
      if (body.success) {
        toast.success(body.responseMessage, {
          position: "top-center",
          autoClose: 900,
        });
        await fetchCartAndAddresses();
      } else {
        toast.error(body.responseMessage || "Remove failed", {
          position: "top-center",
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Server error", { position: "top-center" });
    }
  };

  // update quantity (increment / decrement API)
  const updateQuantity = async (cartId, action) => {
    if (!user?.id) return;

    // use the endpoints you specified in controller
    const endpoint =
      action === "increment"
        ? `https://goldenhides.in/api/user/cart/quantity/increment?userId=${user.id}&cartId=${cartId}`
        : `https://goldenhides.in/api/user/cart/quantity/decrement?userId=${user.id}&cartId=${cartId}`;

    try {
      const res = await fetch(endpoint, { method: "GET" });
      const body = await res.json();

      if (body.success) {
        toast.success(
          action === "increment" ? "Quantity increased" : "Quantity decreased",
          {
            position: "top-center",
            autoClose: 600,
          }
        );
        await fetchCartAndAddresses(); // refresh updated cart
      } else {
        toast.error(body.responseMessage || "Update failed", {
          position: "top-center",
        });
      }
    } catch (err) {
      console.error("Quantity update failed", err);
      toast.error("Server error", { position: "top-center" });
    }
  };

  // Checkout: call your order create endpoint, then open Razorpay
  const checkout = async () => {
    if (!user?.id) {
      toast.error("Please login to checkout", { position: "top-center" });
      return;
    }

    // <<< NEW: if there are NO active addresses, redirect user to add-address page >>>
    if (!addresses || addresses.length === 0) {
      toast.info("Please add a delivery address first.", {
        position: "top-center",
        autoClose: 1000,
      });
      // close drawer then navigate to add address page
      onClose?.();
      navigate("/delivery/address/add");
      return;
    }

    // if active addresses exist, ensure one is selected
    if (
      addresses.length > 0 &&
      (!selectedAddressId || selectedAddressId === "")
    ) {
      toast.error("Please select delivery address", { position: "top-center" });
      return;
    }

    try {
      const url = `https://goldenhides.in/api/user/order/create?userId=${user.id}&deliveryAddressId=${selectedAddressId}`;
      const res = await fetch(url, { method: "PUT" });
      const body = await res.json();

      if (!body) {
        toast.error("Invalid server response", { position: "top-center" });
        return;
      }

      if (!body.success) {
        toast.error(body.responseMessage || "Checkout failed", {
          position: "top-center",
        });
        return;
      }

      const options = body.razorPayRequest;
      if (!options) {
        toast.error("Payment gateway error", { position: "top-center" });
        return;
      }

      // Attach handler so we report payment result back to your server
      options.handler = async function (response) {
        try {
          // ensure order id is included (some servers expect razorpay_order_id)
          if (!response.razorpay_order_id && options.orderId) {
            response.razorpay_order_id = options.orderId;
          }

          // send payment response to your server
          const r = await fetch(
            "https://goldenhides.in/api/user/razorpPay/response",
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(response),
            }
          );
          const respBody = await r.json();
          if (respBody.success) {
            toast.success(respBody.responseMessage || "Payment success", {
              position: "top-center",
              autoClose: 1200,
            });
            // refresh cart & close drawer
            await fetchCartAndAddresses();
            onClose?.();
            // optionally navigate to my orders page after a short delay
            setTimeout(() => {
              navigate("/user/myorder");
            }, 1000);
          } else {
            toast.error(
              respBody.responseMessage || "Payment verification failed",
              {
                position: "top-center",
              }
            );
          }
        } catch (err) {
          console.error("razorpay handler error", err);
          toast.error("Server error during payment handling", {
            position: "top-center",
          });
        }
      };

      // open razorpay checkout if available
      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", async function (response) {
          // attach orderId so backend has context
          if (!response.razorpay_order_id && options.orderId) {
            response.razorpay_order_id = options.orderId;
          }
          try {
            await fetch("https://goldenhides.in/api/user/razorpPay/response", {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(response),
            });
          } catch (e) {
            console.warn("failed to report payment failure", e);
          }
          toast.error("Payment failed", { position: "top-center" });
        });
        rzp.open();
      } else {
        toast.error("Payment gateway is not available", {
          position: "top-center",
        });
      }
    } catch (err) {
      console.error("checkout error", err);
      toast.error("Checkout failed", { position: "top-center" });
    }
  };

  // drawer classes reuse your cart-drawer CSS so it appears on right
  return (
    <>
      <div
        className={`cart-drawer-backdrop ${show ? "visible" : ""}`}
        onClick={onClose}
      />
      <aside
        className={`cart-drawer ${show ? "open" : ""}`}
        aria-hidden={!show}
      >
        <div className="cart-drawer-header d-flex align-items-center justify-content-between">
          <h5 className="mb-0">
            My Cart <small className="text-muted">({cartItems.length})</small>
          </h5>
          <button
            className="btn btn-link p-0 cart-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="cart-drawer-body">
          {cartItems.length === 0 ? (
            <div className="text-center py-4">
              <strong>Your cart is empty</strong>
            </div>
          ) : (
            <>
              <div className="cart-items-list">
                {cartItems.map((it) => {
                  const imageUrl = it.productImage
                    ? `https://goldenhides.in/api/product/${it.productImage}`
                    : "";

                  // compute unit price and item total safely
                  const unitPrice = Number(it.price ?? 0);
                  const quantity = Number(it.quantity ?? 0);
                  const itemTotal = Number((unitPrice * quantity).toFixed(2));

                  return (
                    <div
                      className="cart-item-card"
                      key={it.cartId || `${it.productId}-${it.size}`}
                    >
                      <div className="cart-item-inner d-flex">
                        <div className="cart-item-thumb">
                          {imageUrl ? (
                            <img src={imageUrl} alt={it.productName} />
                          ) : (
                            <div className="placeholder-img" />
                          )}
                        </div>

                        <div className="cart-item-main flex-grow-1">
                          <div className="d-flex justify-content-between">
                            <div>
                              <div
                                style={{
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  color: "#154936",
                                  transition: "color 0.2s ease",
                                }}
                                title={it.productName}
                                onClick={() => {
                                  onClose?.(); // close the drawer first
                                  // navigate to product page - make sure route exists
                                  navigate(
                                    `/product/${it.productId}/category/${it.categoryId}`
                                  );
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.textDecoration =
                                    "underline")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.textDecoration =
                                    "none")
                                }
                              >
                                {getShortDescription(it.productName, 3)}
                                {it.size && (
                                  <span
                                    style={{
                                      marginLeft: 8,
                                      fontSize: 12,
                                      padding: "2px 8px",
                                      borderRadius: 12,
                                      background: "#f2f6f2",
                                      color: "#154936",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {it.size}
                                  </span>
                                )}
                              </div>

                              {/* short product description (use correct field) */}
                              <div className="small text-muted mt-1">
                                {getShortDescription(it.productDescription, 4)}
                              </div>

                              {/* unit price (muted) */}
                              <div className="small text-muted mt-2">
                                ₹ {unitPrice.toFixed(2)} &nbsp;•&nbsp; Qty:{" "}
                                {quantity}
                              </div>
                            </div>

                            <div className="text-end">
                              {/* item total */}
                              <div className="cart-item-price">
                                ₹ {itemTotal.toFixed(2)}
                              </div>
                              <button
                                className="btn btn-link text-danger p-0 cart-remove-btn"
                                onClick={() => removeItem(it.cartId)}
                                aria-label="Remove"
                              >
                                <FaTrashAlt size={14} />
                              </button>
                            </div>
                          </div>

                          <div className="qty-controls mt-3">
                            <button
                              className="qty-btn"
                              onClick={() =>
                                updateQuantity(it.cartId, "decrement")
                              }
                              aria-label="Decrease"
                            >
                              <FaMinus />
                            </button>
                            <div className="qty-number">{quantity}</div>
                            <button
                              className="qty-btn"
                              onClick={() =>
                                updateQuantity(it.cartId, "increment")
                              }
                              aria-label="Increase"
                            >
                              <FaPlus />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="cart-drawer-footer">
          <div style={{ marginBottom: 12 }}>
            <div className="small text-muted">Delivery Address</div>
            {addresses.length === 0 ? (
              <div className="text-danger">
                No active delivery address — please add one in profile
              </div>
            ) : (
              <select
                className="form-control"
                value={selectedAddressId}
                onChange={(e) => setSelectedAddressId(e.target.value)}
              >
                <option value="">Select Delivery Address</option>
                {addresses.map((a) => (
                  <option value={a.id} key={a.id}>
                    {a.addressLine} {a.downtown || ""} {a.city || ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="d-flex justify-content-between align-items-center totals-row">
            <div>
              <div className="small text-muted">Items</div>
              <strong>{cartItems.length}</strong>
            </div>

            <div style={{ textAlign: "right" }}>
              <div className="small text-muted">Total</div>
              <strong>₹ {Number(totalPrice || 0).toFixed(2)}</strong>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <button
              className="btn proceed-btn w-100 py-2"
              onClick={checkout}
              disabled={cartItems.length === 0}
            >
              Pay ₹ {Number(totalPrice || 0).toFixed(2)}
            </button>
          </div>
        </div>
      </aside>

      <ToastContainer />
    </>
  );
};

export default CustomerCartDrawer;
